import { initializeDynamicContent } from './lazy-loading';

export const fetchPosts = () => {
    const triggers = document.querySelectorAll('.load-posts');
    const loadingClass = 'is-loading';
    const state = new Map();

    const updateState = (element) => {
        let name = element.name.replace('[]', '');
        let value;

        // Any non-pagination interaction (filter, search, select, tab) must
        // return to page 1. Otherwise a `paged` left in state from a prior
        // pagination click rides along with the new filter, and the smaller
        // filtered result set has no such page — the server returns zero
        // results. Pagination clicks (.page-numbers) set `paged` themselves
        // just below, so they're exempt.
        if (!element.classList.contains('page-numbers')) {
            state.delete('paged');
        }

        if (element.type === 'checkbox') {
            value = Array.from(document.querySelectorAll(`input[name="${element.name}"]:checked`))
                .map(cb => cb.value);
        } else if (element.classList.contains('page-numbers')) { // Pagination links
            // `state` is a Map — reading `state.page` returned undefined,
            // which made prev/next emit NaN (serialized as null on the wire,
            // collapsed to page 1 server-side, leaving the pagination UI
            // with no current-page highlight). Read the current page from
            // the Map properly, defaulting to 1 if no prior pagination click.
            const currentPaged = parseInt(state.get('paged')?.value, 10) || 1;
            let pageNum;
            if (element.classList.contains('prev')) {
                pageNum = Math.max(1, currentPaged - 1);
            } else if (element.classList.contains('next')) {
                pageNum = currentPaged + 1;
            } else {
                pageNum = parseInt(element.textContent) || 1;
            }
            name = 'paged';
            value = pageNum;
        } else {
            value = element.value;
        }

        if ((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && value)) {
            state.set(name, {
                value,
                dataset: element.dataset
            });
        } else {
            state.delete(name);
        }

        // console.log('State updated:', state);
    };

    const getQueryParams = () => {
        const params = {};
        
        state.forEach((value, key) => {
            if (value.dataset?.taxQuery) {
                const taxQuery = JSON.parse(value.dataset.taxQuery);
                taxQuery.terms = value.value;
                params.taxQuery = params.taxQuery || [];
                params.taxQuery.push(taxQuery);
            } else if (value.dataset?.metaQuery) {
                const metaQuery = JSON.parse(value.dataset.metaQuery);
                metaQuery.value = value.value;
                params.metaQuery = params.metaQuery || [];
                params.metaQuery.push(metaQuery);
            } else {
                params[key] = value.value;
            }
        });
        
        return params;
    };

    const triggerFetch = (target, nonce, paged = 1) => {
        // `target` is now a unique DOM id (e.g. "archive-block_xxx") rather
        // than a class. Each post-list block emits its own id so multiple
        // post-list blocks on a page no longer cross-update each other.
        const container = document.getElementById(target);
        if (!container) return;
        const params = getQueryParams();
        fetchContent(nonce, params, container);
    };

    const setLoadingState = (isLoading, container) => {
        if (isLoading) {
            container.classList.add(loadingClass);
            triggers.forEach(trigger => {
                trigger.setAttribute('disabled', true);
                trigger.classList.add('is-loading');
            });
        } else {
            container.classList.remove(loadingClass);
            triggers.forEach(trigger => {
                trigger.removeAttribute('disabled');
                trigger.classList.remove('is-loading');
            });
        }
    };

    const sanitizeURLParam = (param) => {
        if (!param) return '';
        return param.toString()
            .replace(/[<>]/g, '')
            .replace(/[^a-zA-Z0-9-_]/g, '')
            .trim();
    };

    // const isValidTerm = (param) => {
    //     const cleanParam = sanitizeURLParam(param);
    //     return validTerms.includes(cleanParam);
    // };

    const updateURLParam = (key, value) => {
        const url = new URL(window.location);
        url.searchParams.set(key, value);
        history.pushState({}, '', url);
    };

    const handlePaginationClick = (e) => {
        const paginationLink = e.target.closest('.page-numbers');
        if (!paginationLink) return;
    
        e.preventDefault();
        updateState(e.target);
        triggerFetch(e.target.dataset.target, e.target.dataset.nonce);
    }

    const handleTriggerClick = (e) => {
        e.preventDefault();

        // console.log('Trigger click:', e.target);

        updateState(e.target);
        triggerFetch(e.target.dataset.target, e.target.dataset.nonce);
    };

    const handleInputChange = (e) => {
        updateState(e.target);

        // Add debounce to prevent too many requests
        clearTimeout(window.searchTimeout);

        window.searchTimeout = setTimeout(() => {
            // fetchContent(input.dataset.paged ?? 1, input.dataset.nonce, queryParams, targetContainer);
            triggerFetch(e.target.dataset.target, e.target.dataset.nonce);
        }, 500);
    };

    const handleSelectChange = (e) => {
        updateState(e.target);
        triggerFetch(e.target.dataset.target, e.target.dataset.nonce);
    };

    const fetchContent = async (nonce, queryParams, container) => {
        // Add post_type from closest .archive container
        // console.log(container);
        const archive = container.closest('.archive');
        queryParams.post_type = archive.dataset.postType;
        queryParams.posts_per_page = archive.dataset.postsPerPage;
        // Round-trip the container id so the AJAX response's pagination
        // links and "Clear Filters" button reference the same target.
        // Without this, every paginated response would emit data-target=""
        // and clicking page 2 would do nothing.
        queryParams.target_id = container.id;
        // Optional excerpt-template variant. Only attach if non-empty so the
        // server-side default fallback kicks in cleanly.
        if (archive.dataset.layout) {
            queryParams.layout = archive.dataset.layout;
        }
        // Block-level WP_Query pre-filter (from post-list's query_configuration
        // ACF field). Merge into queryParams so pagination/filter requests
        // preserve the pre-filter — without this, page 2 would show all posts.
        // tax_query merges additively to coexist with any active filter widgets.
        if (archive.dataset.queryArgs) {
            try {
                const queryArgs = JSON.parse(archive.dataset.queryArgs);
                if (queryArgs && typeof queryArgs === 'object') {
                    for (const [key, value] of Object.entries(queryArgs)) {
                        if (key === 'tax_query' || key === 'taxQuery') {
                            queryParams.taxQuery = (queryParams.taxQuery || []).concat(value);
                        } else {
                            queryParams[key] = value;
                        }
                    }
                }
            } catch (e) {
                console.warn('Invalid data-query-args JSON on .archive', e);
            }
        }
        setLoadingState(true, container);

        // console.log('QueryParams Sent: ', queryParams);

        try {
            const urlPath = window.location.pathname;
            let url = urlPath;
            const rawUrlParams = window.location.search;
            const urlParams = new URLSearchParams(rawUrlParams);
            const tabValue = urlParams.get('tab');

            // if (isValidTerm(tabValue)) {
            //     url = `${urlPath}${rawUrlParams}`;
            // }

            const response = await fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'fetch_posts',
                    nonce,
                    url,
                    query_params: JSON.stringify(queryParams)
                })
            });

            const data = await response.json();

            // console.log('fetchContent response', { containerId: container.id, success: data.success,       
            // contentLength: data.data?.content?.length });     
            // console.log('Data Received: ', data);
            
            if (data.success) {
                container.innerHTML = data.data.content;
                
                // Initialize lazy loading on the container with new content
                initializeDynamicContent(container);
            }
        } catch (error) {
            console.error('Error fetching posts: ', error);
        } finally {
            setLoadingState(false, container);
        }
    };

    // This function appends the "value" to either the "tax_query" or the "meta_query", 
    // and automatically formats it to appear in the respective parameter.
    const processFilterParams = (queryParams, value) => {
        if (queryParams.taxQuery) {
            const taxQuery = JSON.parse(queryParams.taxQuery);
            taxQuery.terms = value;
            queryParams.taxQuery = [taxQuery];
        } 
        else if (queryParams.metaQuery) {
            const metaQuery = JSON.parse(queryParams.metaQuery);
            metaQuery.value = value;
            queryParams.metaQuery = [metaQuery];
        }
        else {
            queryParams[queryParams.queryKey] = value;
        }
        
        return queryParams;
    };

    document.addEventListener('DOMContentLoaded', () => {
        // Initialize state
        document.querySelectorAll('.load-posts, .load-posts--select, .load-posts--input').forEach(filter => {
            if (filter.type === 'checkbox' || filter.type === 'radio') {
                if (filter.checked) {
                    state.set(filter.name, filter.value);
                }
            } else if (filter.value) {
                state.set(filter.name, filter.value);
            }
        });

        // Reset state
        document.body.addEventListener('click', (event) => {
            const resetButton = event.target.closest('.load-posts__reset');
            if (!resetButton) return;

            event.preventDefault();
            
            state.clear();
            document.querySelectorAll('input, select').forEach(input => {
                if (input.type === 'checkbox' || input.type === 'radio') {
                    input.checked = false;
                } else {
                    input.value = '';
                }
            });
            
            triggerFetch(resetButton.dataset.target, resetButton.dataset.nonce);
         });
        
        // Pagination
        document.body.addEventListener('click', (event) => {
            const paginationWrapper = event.target.closest('.pagination--ajax');
            if (!paginationWrapper) return;
            handlePaginationClick(event);
        });

        document.body.addEventListener('click', (event) => {
            const trigger = event.target.closest('.load-posts');
            if (!trigger) return;
            handleTriggerClick(event);
        });

        // Tabs visual active-state toggle. Lives separately from the
        // .load-posts click handler above (which fires the fetch) so each
        // concern stays isolated — this is purely a UI swap.
        document.body.addEventListener('click', (event) => {
            const tab = event.target.closest('.tab');
            if (!tab) return;
            const tabsList = tab.closest('.tabs');
            if (!tabsList) return;
            tabsList.querySelectorAll('.tab').forEach(t => t.classList.remove('is-active'));
            tab.classList.add('is-active');
        });

        document.body.addEventListener('input', (event) => {
            const trigger = event.target.closest('.load-posts--input');
            if (!trigger) return;
            handleInputChange(event);
            });

            document.body.addEventListener('change', (event) => {
            const trigger = event.target.closest('.load-posts--select');
            if (!trigger) return;
            handleSelectChange(event);
        });
    });
};